import { useDispatch } from 'react-redux';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';

import {
  useCreateVersionMutation,
  useDeleteVersionMutation,
} from '../hooks/useBuyingPlanVersionMutation.js';
import { useRefetchBuyingPlanInfo } from '../hooks/useGetBuyingPlanData.js';
import { acceptRecommendationActions } from '../redux/action/api/RecommendationsApi.js';

export const useCreateNewVersion = () => {
  const { t } = useTranslation('@myn/planning');
  const dispatch = useDispatch();

  const createNewVersion = useCreateVersionMutation();
  const refetchBuyingPlanInfo = useRefetchBuyingPlanInfo();

  const handleCreateNewVersion = async ({
    versionId,
    recoId,
  }: {
    versionId: number;
    recoId?: number;
  }) => {
    try {
      const { name } = await createNewVersion({ activeVersionId: versionId });
      refetchBuyingPlanInfo();
      enqueueSnackbar({
        variant: 'success',
        title: t('New version created'),
        subTitle: t('{{name}} created successfully', { name }),
        autoHideDuration: 6000,
        removable: true,
      });

      if (recoId) {
        dispatch(acceptRecommendationActions.request({ id: recoId }));
      }
    } catch (error) {
      return enqueueSnackbar({
        variant: 'error',
        title: t('Error creating version'),
        subTitle: t('Version copy was not created'),
        autoHideDuration: 6000,
        removable: true,
      });
    }
  };

  return { handleCreateNewVersion };
};

export const useDeleteVersion = () => {
  const { mutateAsync: deleteVersion, isPending } = useDeleteVersionMutation();
  const refetchBuyingPlanInfo = useRefetchBuyingPlanInfo();

  const handleDeleteVersion = async ({ versionId }: { versionId: number }) => {
    await deleteVersion({ versionId });
    refetchBuyingPlanInfo();
  };

  return { handleDeleteVersion, isPending };
};
